import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, Plus, Edit2, Trash2, Loader2, ClipboardList, Navigation2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import VehicleTrackModal from '../../components/VehicleTrackModal';

const VendorVehicleList = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [vehicles, setVehicles] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [trackingVehicle, setTrackingVehicle] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json'
  }), []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (vendorFilter !== 'all') params.append('vendor_id', vendorFilter);
      if (typeFilter !== 'all') params.append('vehicle_type_id', typeFilter);
      if (search) params.append('search', search);
      const [vehRes, vendRes, vtRes] = await Promise.all([
        fetch(`${backendUrl}/api/vendor-vehicles?${params}`, { headers: getAuthHeaders() }),
        fetch(`${backendUrl}/api/vendors`, { headers: getAuthHeaders() }),
        fetch(`${backendUrl}/api/vehicle-types`, { headers: getAuthHeaders() }),
      ]);
      if (vehRes.ok) setVehicles(await vehRes.json());
      if (vendRes.ok) { const v = await vendRes.json(); setVendors(v.items || []); }
      if (vtRes.ok) setVehicleTypes(await vtRes.json());
    } catch { toast({ title: "Error", description: "Failed to load data", variant: "destructive" }); }
    finally { setLoading(false); }
  }, [backendUrl, getAuthHeaders, vendorFilter, typeFilter, search, toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const timer = setTimeout(fetchData, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await fetch(`${backendUrl}/api/vendor-vehicles/${deleteTarget.id}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (!res.ok) throw new Error();
      toast({ title: "Deleted", description: "Vehicle removed" });
      fetchData();
    } catch { toast({ title: "Error", description: "Failed to delete", variant: "destructive" }); }
    finally { setDeleteTarget(null); }
  };

  const isExpired = (dateStr) => dateStr && new Date(dateStr) < new Date();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-dark font-manrope">Vendor Vehicles</h1>
          <p className="text-text-secondary font-ibm mt-1">Manage fleet vehicles by vendor</p>
        </div>
        <Button data-testid="add-vehicle-btn" onClick={() => navigate('/fleet/vehicles/add')}
          className="bg-accent-blue hover:bg-accent-blue/90 text-white font-ibm font-semibold h-11 px-6 rounded-lg">
          <Plus className="w-5 h-5 mr-2" />Add Vehicle
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-primary-blue text-white rounded-xl shadow-lg p-6">
        <div className="flex flex-wrap items-center gap-4">
          <h3 className="text-xl font-semibold font-manrope mr-2">All Vehicles ({vehicles.length})</h3>
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60" />
            <Input data-testid="vehicle-search-input" placeholder="Search by name or reg. no..." value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/60 focus:bg-white/20 font-ibm" />
          </div>
          <Select value={vendorFilter} onValueChange={setVendorFilter}>
            <SelectTrigger data-testid="vendor-filter" className="w-44 bg-white/10 border-white/20 text-white font-ibm">
              <SelectValue placeholder="All Vendors" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Vendors</SelectItem>
              {vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.business_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger data-testid="type-filter" className="w-44 bg-white/10 border-white/20 text-white font-ibm">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {vehicleTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.type_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Table */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-7 h-7 animate-spin text-accent-blue" />
          </div>
        ) : vehicles.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <p className="text-text-secondary font-ibm">No vehicles found</p>
            <Button onClick={() => navigate('/fleet/vehicles/add')} className="bg-accent-blue text-white font-ibm">
              <Plus className="w-4 h-4 mr-2" />Add First Vehicle
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="vehicles-table">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['#','Vendor','Vehicle Name','Reg. Number','Type','Size','Owner','Mobile','Permit Expiry','Actions'].map(h => (
                    <th key={h} className="px-4 py-3.5 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {vehicles.map((v, idx) => (
                  <tr key={v.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-sm text-text-secondary font-ibm">{idx + 1}</td>
                    <td className="px-4 py-3 text-sm font-medium text-text-dark font-ibm whitespace-nowrap">{v.vendor_name || '—'}</td>
                    <td className="px-4 py-3 text-sm text-text-dark font-ibm">{v.vehicle_name || '—'}</td>
                    <td className="px-4 py-3 text-sm font-mono font-medium text-text-dark">{v.registration_number}</td>
                    <td className="px-4 py-3 text-sm text-text-secondary font-ibm whitespace-nowrap">{v.vehicle_type_name || '—'}</td>
                    <td className="px-4 py-3 text-sm text-text-secondary font-ibm">{v.vehicle_size || '—'}</td>
                    <td className="px-4 py-3 text-sm text-text-secondary font-ibm">{v.owner_name || '—'}</td>
                    <td className="px-4 py-3 text-sm text-text-secondary font-ibm">{v.mobile_1 || '—'}</td>
                    <td className="px-4 py-3">
                      {v.permit_expiry_date ? (
                        <Badge className={`font-ibm border-0 text-xs ${isExpired(v.permit_expiry_date) ? 'bg-accent-red/15 text-accent-red' : 'bg-accent-green/15 text-green-700'}`}>
                          {v.permit_expiry_date}
                        </Badge>
                      ) : <span className="text-sm text-text-secondary font-ibm">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          data-testid={`track-vehicle-${v.id}`}
                          onClick={() => setTrackingVehicle(v)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-blue-50 rounded-lg"
                          title="Track Vehicle"
                        >
                          <Navigation2 className="w-4 h-4 text-blue-500" />
                        </button>
                        <button data-testid={`edit-vehicle-${v.id}`} onClick={() => navigate(`/fleet/vehicles/edit/${v.id}`)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg" title="Edit">
                          <Edit2 className="w-4 h-4 text-accent-blue" />
                        </button>
                        <button data-testid={`records-vehicle-${v.id}`} onClick={() => navigate(`/fleet/vehicles/edit/${v.id}`, { state: { tab: 'Documents' } })}
                          className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg" title="View Records">
                          <ClipboardList className="w-4 h-4 text-text-secondary" />
                        </button>
                        <button data-testid={`delete-vehicle-${v.id}`} onClick={() => setDeleteTarget(v)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-accent-red/10 rounded-lg" title="Delete">
                          <Trash2 className="w-4 h-4 text-accent-red" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <AlertDialog open={!!deleteTarget} onOpenChange={v => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Vehicle</AlertDialogTitle>
            <AlertDialogDescription>Delete vehicle "{deleteTarget?.registration_number}"? All associated compliance and maintenance records will also be deleted.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-accent-red hover:bg-accent-red/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {trackingVehicle && (
        <VehicleTrackModal
          vehicle={trackingVehicle}
          onClose={() => setTrackingVehicle(null)}
        />
      )}
    </div>
  );
};

export default VendorVehicleList;
